/**
 * Shared vessel category icons (Lucide components) for map, tooltips, and dropdowns.
 * Whale is a custom icon (marine animal, not a vessel).
 * Cargo ship uses custom SVG from assets/icons/vessels/icon-cargo-ship.svg.
 */
import React from 'react';
import { Ship, Sailboat, Anchor, Fish, Droplets, Shield } from 'lucide-react';

/** Whale icon (marine animal, not a vessel) – 24x24, Lucide-style stroke, side view with tail */
const WhaleIcon = ({ className, size = 24, strokeWidth = 2, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M3 11c0-2 3-4 7-4s6 1.5 6.5 4c.5 2.5 0 5-2.5 6.5S11 19 11 21" />
    <path d="M16.5 11c.5 1.5 2 2.5 4 2.5" />
    <path d="M20.5 9.5L23 12l-2.5 2.5" />
  </svg>
);

/** Cargo ship icon – custom SVG (chimney, bridge, deck, containers, hull). Matches assets/icons/vessels/icon-cargo-ship.svg. Use fill="none" on shapes so only stroke (currentColor) shows, same as other icons. */
const CargoShipIcon = ({ className, size = 24, strokeWidth = 2, ...props }) => {
  const w = size * (128 / 96);
  const strokeProps = { fill: 'none', stroke: 'currentColor', strokeWidth, strokeLinecap: 'round', strokeLinejoin: 'round' };
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={size}
      viewBox="0 0 128 96"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...props}
    >
      <line x1="32" y1="12" x2="32" y2="20" {...strokeProps} />
      <rect x="20" y="20" width="24" height="16" {...strokeProps} />
      <rect x="16" y="36" width="40" height="12" {...strokeProps} />
      <line x1="20" y1="42" x2="28" y2="42" {...strokeProps} />
      <rect x="60" y="32" width="18" height="16" {...strokeProps} />
      <rect x="78" y="32" width="18" height="16" {...strokeProps} />
      <rect x="60" y="48" width="18" height="14" {...strokeProps} />
      <rect x="78" y="48" width="18" height="14" {...strokeProps} />
      <rect x="96" y="48" width="18" height="14" {...strokeProps} />
      <path d="M8 48H112L120 48L104 80H24L16 64L8 48Z" {...strokeProps} />
      <line x1="30" y1="68" x2="58" y2="68" {...strokeProps} />
      <line x1="66" y1="68" x2="96" y2="68" {...strokeProps} />
    </svg>
  );
};

const VESSEL_ICON_MAP = {
  whale: WhaleIcon,
  whales: WhaleIcon,
  fishing_boat: Fish,
  fishing_vessel: Fish,
  fishing: Fish,
  sailboat: Sailboat,
  sailing_vessel: Sailboat,
  sailing_boat: Sailboat,
  yacht: Sailboat,
  cruise_ship: Ship,
  passenger_ship: Ship,
  ferry: Ship,
  passenger: Ship,
  cargo_ship: CargoShipIcon,
  container_ship: CargoShipIcon,
  cargo: CargoShipIcon,
  tanker: Droplets,
  oil_tanker: Droplets,
  patrol_boat: Shield,
  military: Shield,
  coast_guard: Shield,
  patrol: Shield,
  defensive_surface_vehicle: Shield,
  defense_usv: Shield,
  defensive_usv: Shield,
  surface_defense_usv: Shield,
  defensive_surface_usv: Shield,
  blue_force_usv: Shield,
  tugboat: Anchor,
  tug: Anchor,
  anchor: Anchor
};

/** Atlas key for Deck.gl IconLayer (must match keys in vesselIconAtlas) */
const VESSEL_ATLAS_KEYS = {
  whale: 'whale',
  whales: 'whale',
  fishing_boat: 'fish',
  fishing_vessel: 'fish',
  fishing: 'fish',
  sailboat: 'sailboat',
  sailing_vessel: 'sailboat',
  sailing_boat: 'sailboat',
  yacht: 'sailboat',
  cruise_ship: 'ship',
  passenger_ship: 'ship',
  ferry: 'ship',
  passenger: 'ship',
  cargo_ship: 'cargo_ship',
  container_ship: 'cargo_ship',
  cargo: 'cargo_ship',
  tanker: 'droplets',
  oil_tanker: 'droplets',
  patrol_boat: 'shield',
  military: 'shield',
  coast_guard: 'shield',
  patrol: 'shield',
  defensive_surface_vehicle: 'shield',
  defense_usv: 'shield',
  defensive_usv: 'shield',
  surface_defense_usv: 'shield',
  defensive_surface_usv: 'shield',
  blue_force_usv: 'shield',
  tugboat: 'anchor',
  tug: 'anchor',
  anchor: 'anchor'
};

/**
 * Returns the Lucide icon component for a vessel common_name (e.g. "fishing_boat" -> Fish).
 * @param {string} commonName - common_name or category string from data
 * @returns {React.ComponentType} Lucide icon component (default: Ship)
 */
export const getVesselIconComponent = (commonName) => {
  if (!commonName || typeof commonName !== 'string') return Ship;
  const key = String(commonName).trim().toLowerCase().replace(/\s+/g, '_');
  return VESSEL_ICON_MAP[key] || Ship;
};

/**
 * Returns the atlas icon key for Deck.gl IconLayer (e.g. "fishing_boat" -> "fish").
 * @param {string} commonName - common_name or category string from data
 * @returns {string} One of: 'ship' | 'fish' | 'package' | 'droplets' | 'shield' | 'sailboat' | 'anchor' | 'whale' | 'cargo_ship'
 */
export const getVesselIconAtlasKey = (commonName) => {
  if (!commonName || typeof commonName !== 'string') return 'ship';
  const key = String(commonName).trim().toLowerCase().replace(/\s+/g, '_');
  return VESSEL_ATLAS_KEYS[key] || 'ship';
};
